import V4API, {clearV4SiteUuidCache, v4ScopeForUuid} from '../../api/V4client';
import {mapV4SiteToV3, v3SiteToV4Body} from './transforms';

const getField = (site, key) => (site?.get ? site.get(key) : site?.[key]);

// Rows lack background.blob (see fetchV4SiteFloorPlan). null = the list failed,
// distinct from [] for an account with no sites.
export async function fetchV4Sites(scope, accountNames = {}) {
    try {
        const res = await V4API.sitesList(scope);
        return (res?.data || [])
            .filter(s => s?.id)
            .map(s => mapV4SiteToV3(s, accountNames[s.owner_id] || ''));
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch V4 sites', e?.response?.status, e?.response?.data || e);
        return null;
    }
}

// A saved V4 row still without its floor plan.
export const needsV4FloorPlan = site => getField(site, 'source') === 'v4'
    && !!(getField(site, 'uuid') || getField(site, 'id'))
    && !getField(site, 'url');

// Merges only the plan into the row, which keeps its account labels. Throws on failure.
export async function fetchV4SiteFloorPlan(site) {
    const id = getField(site, 'uuid') || getField(site, 'id');
    const owner = getField(site, 'v4OwnerId');
    const res = await V4API.sitesDetail(id, owner ? v4ScopeForUuid(owner) : undefined);
    const detail = res?.data;
    if (!detail?.id) throw new Error('no site in the response');
    const {url, opacity} = mapV4SiteToV3(detail);
    return site.set('url', url).set('opacity', opacity).set('v4Data', detail);
}

// Keeps the plan once (in url, not v4Data). Throws on failure.
export async function fetchV4Site(id, scope, accountNames = {}) {
    const res = await V4API.sitesDetail(id, scope);
    const detail = res?.data;
    if (!detail?.id) throw new Error('no site in the response');
    const site = mapV4SiteToV3(detail, accountNames[detail.owner_id] || '');
    if (!detail.background?.blob) return site;
    const {blob, ...background} = detail.background;
    return {...site, v4Data: {...detail, background}};
}

export async function saveV4Site(site, scope) {
    const body = v3SiteToV4Body(site);
    const id = getField(site, 'uuid') || getField(site, 'id');
    const isNew = !id || id === 0;
    // Update is a full replace: a row without its plan would erase it.
    if (!isNew && !body.background) throw new Error('its floor plan is not loaded');
    const res = isNew
        ? await V4API.sitesCreate(body, scope)
        : await V4API.sitesUpdate(id, body, scope);
    // The uuid lookup memoises misses; a new site would stay out of the geofence Site picker.
    clearV4SiteUuidCache();
    return res?.data;
}

export async function deleteV4Site(site, scope) {
    const id = getField(site, 'uuid') || getField(site, 'id');
    if (!id) return;
    await V4API.sitesDelete(id, scope);
    clearV4SiteUuidCache();
}
